import React, { useEffect, useMemo, useState } from "react";
import { ORG_FORM_ROLE_OPTIONS } from "../../utils/orgChartModel";

const emptyForm = {
  name: "",
  designation: "",
  roleRaw: ORG_FORM_ROLE_OPTIONS[0].value,
  department: "",
  parentId: "",
};

/**
 * @param {"add"|"edit"|"closed"} mode
 * @param {object | null} editingRow — row when mode is edit
 * @param {Array<{id: string, selfDisplay: string}>} managerOptions — people who can be manager (excludes editing id)
 * @param {(values: typeof emptyForm) => void} onSave
 * @param {() => void} onCancel
 */
export function OrgPersonForm({ mode, editingRow, managerOptions, onSave, onCancel, disabled }) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (mode === "edit" && editingRow) {
      setForm({
        name: editingRow.name || "",
        designation: editingRow.designation || "",
        roleRaw: editingRow.roleRaw || ORG_FORM_ROLE_OPTIONS[0].value,
        department: editingRow.department || "",
        parentId: editingRow.parentId || "",
      });
    } else if (mode === "add") {
      setForm({ ...emptyForm });
    }
  }, [mode, editingRow]);

  const open = mode === "add" || mode === "edit";
  const title = mode === "edit" ? "Edit person" : "Add person";

  const selectOptions = useMemo(() => {
    const base = [{ value: "", label: "- None (top level) -" }];
    return base.concat((managerOptions || []).map((m) => ({ value: m.id, label: m.selfDisplay })));
  }, [managerOptions]);

  if (!open) return null;

  return (
    <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm">
      <h4 className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">{title}</h4>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Full name</span>
          <input
            className="mt-1 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:border-[color:var(--color-border-strong)]"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            disabled={disabled}
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Designation</span>
          <input
            className="mt-1 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:border-[color:var(--color-border-strong)]"
            value={form.designation}
            onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
            disabled={disabled}
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Role</span>
          <select
            className="mt-1 w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-3 py-2 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:border-[color:var(--color-border-strong)]"
            value={form.roleRaw}
            onChange={(e) => setForm((f) => ({ ...f, roleRaw: e.target.value }))}
            disabled={disabled}
          >
            {ORG_FORM_ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.value}
              </option>
            ))}
          </select>
        </label>
        <label className="block sm:col-span-2 lg:col-span-1">
          <span className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Department</span>
          <input
            className="mt-1 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:border-[color:var(--color-border-strong)]"
            value={form.department}
            onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
            disabled={disabled}
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Reports to</span>
          <select
            className="mt-1 w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-3 py-2 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:border-[color:var(--color-border-strong)]"
            value={form.parentId}
            onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value }))}
            disabled={disabled}
          >
            {selectOptions.map((o) => (
              <option key={o.value || "none"} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onSave?.(form)}
          className="rounded-xl bg-[color:var(--color-accent-fill)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-sm hover:opacity-90 disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onCancel?.()}
          className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] hover:bg-[color:var(--color-bg-alt)] disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
